import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Printer, Hash } from 'lucide-react';
import JsBarcode from 'jsbarcode';

const LABEL_SIZES = [
  { id: 'small', label: 'Small (40x20mm)', w: 200, h: 100 },
  { id: 'medium', label: 'Medium (50x30mm)', w: 260, h: 140 },
  { id: 'large', label: 'Large (60x40mm)', w: 320, h: 180 },
];

const BarcodeLabel = ({ product, size, showPrice }) => {
  const svgRef = useRef(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!product.barcode || !svgRef.current) { setOk(false); return; }
    try {
      const isEan = /^\d{12,13}$/.test(product.barcode);
      JsBarcode(svgRef.current, product.barcode, {
        format: isEan ? 'EAN13' : 'CODE128',
        width: 1.5,
        height: 45,
        displayValue: true,
        fontSize: 11,
        margin: 4,
        lineColor: '#000',
        background: '#fff',
      });
      setOk(true);
    } catch {
      try {
        JsBarcode(svgRef.current, product.barcode, {
          format: 'CODE128', width: 1.5, height: 45,
          displayValue: true, fontSize: 11, margin: 4,
        });
        setOk(true);
      } catch { setOk(false); }
    }
  }, [product.barcode]);

  return (
    <div
      style={{ width: size.w, height: size.h }}
      className="bg-white border border-dashed border-slate-300 flex flex-col items-center justify-center p-1 flex-shrink-0"
    >
      <p className="text-[9px] font-bold text-center truncate max-w-full">{product.name}</p>
      {product.barcode ? (
        <svg ref={svgRef} className={ok ? '' : 'hidden'} />
      ) : (
        <div className="flex items-center gap-1 text-slate-400 text-[9px]">
          <Hash className="w-2.5 h-2.5" /> No barcode
        </div>
      )}
      {showPrice && product.barcode && ok && (
        <p className="text-[10px] font-bold">{'\u20AC'}{product.price?.toFixed(2)}</p>
      )}
    </div>
  );
};

const BulkBarcodePrintModal = ({ products, onClose }) => {
  const [labelSize, setLabelSize] = useState('medium');
  const [copies, setCopies] = useState(1);
  const [showPrice, setShowPrice] = useState(true);
  const [skipNoBarcode, setSkipNoBarcode] = useState(true);

  const size = LABEL_SIZES.find(s => s.id === labelSize);
  const printable = skipNoBarcode ? products.filter(p => p.barcode) : products;
  const totalLabels = printable.length * copies;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    // Build labels HTML with inline JsBarcode via SVG
    const labelsHtml = printable.flatMap(p => {
      return Array.from({ length: copies }, (_, ci) => {
        const svgId = `bc-${p.id}-${ci}`;
        return `
          <div class="label" style="width:${size.w}px;height:${size.h}px;border:1px dashed #ccc;display:inline-flex;flex-direction:column;align-items:center;justify-content:center;padding:6px;margin:4px;page-break-inside:avoid;box-sizing:border-box;">
            <div style="font-size:10px;font-weight:700;text-align:center;margin-bottom:2px;max-width:${size.w - 16}px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">
              ${p.name}
            </div>
            ${p.barcode ? `<svg id="${svgId}"></svg>` : '<div style="color:#999;font-size:9px;">No barcode</div>'}
            ${showPrice && p.barcode ? `<div style="font-size:13px;font-weight:700;margin-top:2px;">\u20AC${p.price?.toFixed(2) || '0.00'}</div>` : ''}
          </div>
        `;
      });
    }).join('');

    // Build JsBarcode calls
    const barcodeScripts = printable.flatMap(p => {
      if (!p.barcode) return [];
      return Array.from({ length: copies }, (_, ci) => {
        const svgId = `bc-${p.id}-${ci}`;
        const isEan = /^\d{12,13}$/.test(p.barcode);
        const fmt = isEan ? 'EAN13' : 'CODE128';
        return `
          try { JsBarcode("#${svgId}", "${p.barcode}", { format:"${fmt}", width:2, height:60, displayValue:true, fontSize:14, margin:6 }); }
          catch(e) { try { JsBarcode("#${svgId}", "${p.barcode}", { format:"CODE128", width:2, height:60, displayValue:true, fontSize:14, margin:6 }); } catch(e2){} }
        `;
      });
    }).join('\n');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Bulk Barcodes - ${printable.length} Products</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.3/dist/JsBarcode.all.min.js"><\/script>
      <style>
        @page { margin: 5mm; }
        body { margin: 0; padding: 8px; font-family: Arial, sans-serif; }
        .labels { display: flex; flex-wrap: wrap; }
        @media print { .label { border: none !important; } .no-print { display: none; } }
      </style>
      </head><body>
        <div class="no-print" style="padding:8px 0;margin-bottom:8px;border-bottom:1px solid #ccc;font-size:14px;color:#666;">
          ${printable.length} products \u00D7 ${copies} = ${totalLabels} labels | Size: ${size.label}
        </div>
        <div class="labels">${labelsHtml}</div>
        <script>
          ${barcodeScripts}
          setTimeout(function(){ window.print(); window.onafterprint=function(){window.close();}; }, 500);
        <\/script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          className="w-[560px] max-h-[85vh] bg-white flex flex-col"
          data-testid="bulk-print-modal"
          onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#1e3a5f]">Bulk Print Barcodes</h3>
            <p className="text-sm text-slate-500">{products.length} products selected</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100" data-testid="bulk-print-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Options */}
        <div className="p-4 space-y-4 flex-shrink-0">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Label Size</label>
              <select value={labelSize} onChange={e => setLabelSize(e.target.value)}
                className="w-full px-2 py-2 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                data-testid="bulk-label-size">
                {LABEL_SIZES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Copies Each</label>
              <input type="number" min="1" max="50" value={copies}
                onChange={e => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-2 py-2 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                data-testid="bulk-copies-input" />
            </div>
            <div className="space-y-2 pt-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={showPrice} onChange={e => setShowPrice(e.target.checked)}
                  className="w-4 h-4 accent-[#22c55e]" data-testid="bulk-show-price" />
                <span className="text-sm text-slate-700">Price</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={skipNoBarcode} onChange={e => setSkipNoBarcode(e.target.checked)}
                  className="w-4 h-4 accent-[#22c55e]" data-testid="bulk-skip-no-barcode" />
                <span className="text-sm text-slate-700">Skip no barcode</span>
              </label>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 border-2 border-slate-100 p-3 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              {printable.length} products &times; {copies} copies = <strong>{totalLabels} labels</strong>
            </span>
            {products.length !== printable.length && (
              <span className="text-xs text-amber-600">
                {products.length - printable.length} skipped (no barcode)
              </span>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto px-4 pb-2 min-h-0">
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Preview (first 6)</label>
          <div className="flex flex-wrap gap-2 bg-slate-50 border-2 border-slate-100 p-3">
            {printable.slice(0, 6).map(p => (
              <BarcodeLabel key={p.id} product={p} size={size} showPrice={showPrice} />
            ))}
            {printable.length > 6 && (
              <div style={{ width: size.w, height: size.h }}
                className="border border-dashed border-slate-300 flex items-center justify-center text-sm text-slate-400">
                +{printable.length - 6} more
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="vf-btn-secondary flex-1">Cancel</button>
          <button onClick={handlePrint} disabled={printable.length === 0}
            className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="bulk-print-btn">
            <Printer className="w-4 h-4" />
            Print {totalLabels} Labels
          </button>
        </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default BulkBarcodePrintModal;
